const EXPERIMENT_ID = 'pdp_sticky_atc_v1';
const STORAGE_KEY = 'caene_ab_pdp_sticky_atc_v1';
const VALID_VARIANTS = new Set(['control', 'treatment']);

export function resolveExperimentVariant({
  search = '',
  storedVariant = null,
  randomValue = Math.random(),
} = {}) {
  const override = new URLSearchParams(search).get('caene_ab_sticky');
  if (VALID_VARIANTS.has(override)) return override;
  if (VALID_VARIANTS.has(storedVariant)) return storedVariant;
  return randomValue < 0.5 ? 'treatment' : 'control';
}

export function shouldShowSticky({ isIntersecting, buttonBottom }) {
  return !isIntersecting && buttonBottom < 0;
}

export function syncStickyVisibility({
  controller,
  primaryButton,
  viewportHeight,
}) {
  const rect = primaryButton.getBoundingClientRect();
  controller.setVisible(
    shouldShowSticky({
      isIntersecting: rect.bottom > 0 && rect.top < viewportHeight,
      buttonBottom: rect.bottom,
    })
  );
}

export function buildArmEventName(baseName, variant) {
  if (!VALID_VARIANTS.has(variant)) {
    throw new Error(`Unknown experiment variant: ${variant}`);
  }
  return `${baseName}_${variant}`;
}

export function createCartSuccessTracker({
  variant,
  subscribeToCart,
  pushEvent,
  eventContext,
}) {
  if (typeof subscribeToCart !== 'function') return null;

  return subscribeToCart((event) => {
    if (event?.source !== 'product-form') return;
    pushEvent({
      ...eventContext,
      event: buildArmEventName('pdp_sticky_atc', variant),
    });
  });
}

export function createStickyController({
  variant,
  isMobile,
  bar,
  primaryButton,
  pushEvent,
  eventContext,
}) {
  let visibleEventSent = false;
  const enabled = variant === 'treatment' && isMobile;

  function setVisible(show) {
    const shouldShow = Boolean(show && enabled && !primaryButton?.disabled);
    bar.classList[shouldShow ? 'add' : 'remove']('sticky-atc--visible');
    bar.setAttribute('aria-hidden', String(!shouldShow));

    if (shouldShow && !visibleEventSent) {
      visibleEventSent = true;
      pushEvent({ event: 'cta_sticky_visible', ...eventContext });
    }
  }

  function click() {
    if (!enabled || primaryButton?.disabled) return;
    pushEvent({ event: 'cta_sticky_click', ...eventContext });
    primaryButton.click();
  }

  return { click, setVisible };
}

function readCookie(name) {
  const prefix = `${encodeURIComponent(name)}=`;
  const match = document.cookie
    .split('; ')
    .find((part) => part.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : null;
}

function readStoredVariant() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || readCookie(STORAGE_KEY);
  } catch (_error) {
    return readCookie(STORAGE_KEY);
  }
}

function persistVariant(variant) {
  try {
    window.localStorage.setItem(STORAGE_KEY, variant);
  } catch (_error) {
    // The first-party cookie keeps assignment stable when storage is blocked.
  }
  document.cookie = `${encodeURIComponent(STORAGE_KEY)}=${encodeURIComponent(variant)}; Max-Age=2592000; Path=/; SameSite=Lax; Secure`;
}

function secureRandomValue() {
  if (window.crypto?.getRandomValues) {
    const value = new Uint32Array(1);
    window.crypto.getRandomValues(value);
    return value[0] / 4294967296;
  }
  return Math.random();
}

export function pushAnalyticsEvent(event, runtime = window) {
  runtime.dataLayer = runtime.dataLayer || [];
  runtime.dataLayer.push(event);

  const { event: eventName, ...parameters } = event;
  if (typeof runtime.gtag !== 'function') {
    runtime.gtag = function gtag() {
      runtime.dataLayer.push(arguments);
    };
  }
  runtime.gtag('event', eventName, parameters);
}

function isKeyboardTarget(target) {
  return Boolean(
    target?.matches?.('input, textarea, select, [contenteditable="true"]')
  );
}

function bootStickyExperiment(bar) {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  if (!mobileQuery.matches) return;

  const sectionId = bar.dataset.sectionId;
  const primaryButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
  if (!primaryButton) return;

  const params = new URLSearchParams(window.location.search);
  const hasQaOverride = VALID_VARIANTS.has(params.get('caene_ab_sticky'));
  const variant = resolveExperimentVariant({
    search: window.location.search,
    storedVariant: readStoredVariant(),
    randomValue: secureRandomValue(),
  });
  if (!hasQaOverride) persistVariant(variant);

  const eventContext = {
    experiment_id: EXPERIMENT_ID,
    experiment_variant: variant,
    experiment_qa: hasQaOverride,
    product_id: bar.dataset.productId,
    product_handle: bar.dataset.productHandle,
  };

  pushAnalyticsEvent({ event: 'pdp_sticky_atc_impression', ...eventContext });
  pushAnalyticsEvent({
    event: buildArmEventName('pdp_sticky_impression', variant),
    ...eventContext,
  });

  const subscribeToCart =
    typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate
      ? (callback) => window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, callback)
      : null;
  createCartSuccessTracker({
    variant,
    subscribeToCart,
    pushEvent: pushAnalyticsEvent,
    eventContext,
  });

  if (variant !== 'treatment') return;

  bar.dataset.experimentVariant = variant;
  const stickyButton = bar.querySelector('[data-sticky-btn]');
  const stickyLabel = bar.querySelector('[data-sticky-label]');
  const primaryLabel = primaryButton.querySelector('.caene-cta__submit-label');

  const syncLabel = () => {
    if (stickyLabel && primaryLabel) {
      stickyLabel.textContent = primaryLabel.textContent.replace(/\s+/g, ' ').trim();
    }
    if (stickyButton) {
      stickyButton.disabled = primaryButton.disabled;
    }
  };
  syncLabel();

  const labelObserver = new MutationObserver(syncLabel);
  if (primaryLabel) {
    labelObserver.observe(primaryLabel, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }
  labelObserver.observe(primaryButton, {
    attributes: true,
    attributeFilter: ['disabled'],
  });

  const controller = createStickyController({
    variant,
    isMobile: true,
    bar,
    primaryButton,
    pushEvent: pushAnalyticsEvent,
    eventContext,
  });

  stickyButton?.addEventListener('click', controller.click);

  const buyBoxObserver = new IntersectionObserver(
    ([entry]) => {
      controller.setVisible(
        shouldShowSticky({
          isIntersecting: entry.isIntersecting,
          buttonBottom: entry.boundingClientRect.bottom,
        })
      );
    },
    { threshold: 0 }
  );
  buyBoxObserver.observe(primaryButton);

  let scrollFrame = null;
  const scheduleVisibilitySync = () => {
    if (scrollFrame !== null) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = null;
      syncStickyVisibility({
        controller,
        primaryButton,
        viewportHeight: window.innerHeight,
      });
    });
  };
  window.addEventListener('scroll', scheduleVisibilitySync, { passive: true });
  window.addEventListener('resize', scheduleVisibilitySync, { passive: true });
  scheduleVisibilitySync();

  document.addEventListener('focusin', (event) => {
    if (isKeyboardTarget(event.target)) {
      bar.classList.add('sticky-atc--keyboard-open');
    }
  });
  document.addEventListener('focusout', () => {
    window.setTimeout(() => {
      if (!isKeyboardTarget(document.activeElement)) {
        bar.classList.remove('sticky-atc--keyboard-open');
      }
    }, 100);
  });
}

if (typeof document !== 'undefined') {
  document
    .querySelectorAll('[data-caene-sticky-atc]')
    .forEach(bootStickyExperiment);
}
